import { z } from 'zod';
import { toIssues } from './materials/catalog.js';
import type { ParseResult } from './voyage/schemas.js';

/**
 * Eğitim ihtiyacı (Crew ▸ Training; ADR 0128 Faz 4 Ek). Kodda eğitim modülü yoktu (menüde "Faz 2"
 * yer tutucusu); tatbikatın Training Need bulgusu bu kaydı açar ve Crew ▸ Training'in ilk yapısı
 * budur. Kayıt gemi verisidir: kişi, konu, gerekçe, hedef tarih, kaynak (tatbikat bulgusu ya da
 * elle); tamamlanınca gün ve nasıl karşılandığı yazılır (doğrulama). Durum saklanmaz.
 *
 * Emsal: AMOS ve ShipManager mürettebat modüllerinde "training requirement / training need" kişiye
 * bağlı, kaynaklı ve hedef tarihlidir; Helm CONNECT tatbikat değerlendirmesinden eğitim görevi açar.
 */

export const TRAINING_NEED_SOURCES = ['drill_finding', 'manual'] as const;
export type TrainingNeedSource = (typeof TRAINING_NEED_SOURCES)[number];

export const TRAINING_NEED_STATUSES = ['open', 'completed', 'cancelled'] as const;
export type TrainingNeedStatus = (typeof TRAINING_NEED_STATUSES)[number];

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const paragraph = (value: string) =>
  value
    .normalize('NFC')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim().replace(/[ \t]+/g, ' '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const day = z
  .string(typed('INVALID_DATE'))
  .regex(DAY, 'INVALID_DATE')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'INVALID_DATE');

function parsed<T>(result: z.ZodSafeParseResult<T>): ParseResult<T> {
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

export const trainingNeedSchema = z.strictObject({
  userId: z.uuid(typed('INVALID_ID')),
  topic: z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(200, 'TOO_LONG')),
  reason: z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().max(2000, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null),
  dueOn: day.nullish().transform((value) => value ?? null),
});
export type TrainingNeedInput = z.output<typeof trainingNeedSchema>;

export function parseTrainingNeed(input: unknown): ParseResult<TrainingNeedInput> {
  return parsed(trainingNeedSchema.safeParse(input));
}

/** Tamamlama: gün ve nasıl karşılandığı (kurs, gemide eğitim, tanıtım; doğrulama kanıtı). */
export const trainingNeedCompletionSchema = z.strictObject({
  completedOn: day,
  note: z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().min(1, 'REQUIRED').max(2000, 'TOO_LONG')),
});
export type TrainingNeedCompletion = z.output<typeof trainingNeedCompletionSchema>;

export function parseTrainingNeedCompletion(input: unknown): ParseResult<TrainingNeedCompletion> {
  return parsed(trainingNeedCompletionSchema.safeParse(input));
}

export function trainingNeedStatus(input: {
  completedOn: string | null;
  cancelReason: string | null;
}): TrainingNeedStatus {
  if (input.cancelReason !== null) return 'cancelled';
  return input.completedOn !== null ? 'completed' : 'open';
}

export function trainingNeedOverdue(
  status: TrainingNeedStatus,
  dueOn: string | null,
  today: string,
): boolean {
  return status === 'open' && dueOn !== null && dueOn < today;
}
